import {switchMap} from "rxjs/operators";
import {ofType} from "redux-observable";

import * as actionTypes from "../constants/actionTypes";
import * as methods from "../constants/methods";
import * as componentTypes from "../constants/componentTypes";
import * as config from "../constants/config";
import * as settings from "../constants/settings";
import * as resources from "../constants/resources";
import * as handlers from "../constants/handlers";
import * as orderStatus from "../constants/orderStatus";
import * as dispensingValidationActions from "../actions/dispensingValidationActions";
import * as rccActions from "../actions/rccActions";
import * as toDispenseTabActions from "../actions/toDispenseTabActions";
import * as filteringHelper from "../helpers/filteringHelper";
import * as formatHelper from "../helpers/formatHelper";
import * as reducerHelper from "../helpers/reducerHelper";

import successfulOrder from "../resources/audio/success-order.mp3";

const saveOrderDetail = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.TO_DISPENSE_SAVE_ORDER_DETAIL),
        switchMap(({payload}) => {
            let activeOrder = state$.value.toDispense.activeOrder;

            let actions = [];

            if (activeOrder !== null && activeOrder.formula !== null) {
                if (!state$.value.rcc.machine.validMethod(methods.METHOD_MACHINE_MANUAL_FORMULA)) {
                    actions.push({
                        type: actionTypes.RCC_REQUEST,
                        payload: {
                            type: methods.METHOD_MACHINE_VALIDATE_FORMULA,
                            params: {
                                formula: activeOrder.formula.getColorantVolumes(),
                                numCansDone: activeOrder.dispensedPackageCount,
                                numCans: activeOrder.packageCount,
                                customCan: activeOrder.package.isCustom()
                            }
                        },
                        method: handlers.TO_DISPENSE_VALIDATE,
                        tabId: payload.tabId,
                        safe: true
                    });
                } else if (state$.value.rcc.machine.validMethod(methods.METHOD_MACHINE_VALIDATE_FORMULA)) {
                    actions.push({
                        type: actionTypes.RCC_REQUEST,
                        payload: {
                            type: methods.METHOD_MACHINE_VALIDATE_FORMULA,
                            params: {
                                formula: activeOrder.formula.getColorantVolumes(),
                                numCansDone: activeOrder.dispensedPackageCount,
                                numCans: activeOrder.packageCount,
                                customCan: activeOrder.package.isCustom()
                            }
                        },
                        method: handlers.TO_DISPENSE_VALIDATE_AND_LOAD_MANUAL,
                        tabId: payload.tabId,
                        safe: true
                    });
                } else {
                    actions.push({
                        type: actionTypes.RCC_REQUEST,
                        payload: {
                            type: methods.METHOD_MACHINE_MANUAL_FORMULA,
                            params: {
                                formula: activeOrder.formula.getColorantVolumes()
                            }
                        },
                        method: handlers.MACHINE_MANUAL_FORMULA_TO_DISPENSE,
                        tabId: payload.tabId,
                        safe: true
                    });
                }
            }

            if (state$.value.module.gammaSoftware) {
                actions.push({
                    type: actionTypes.MENU_ADD_NEW_TO_DISPENSE_TAB
                });
            }

            if (state$.value.module.redlikeLightSoftware && activeOrder !== null) {
                let baseName = activeOrder.base ? activeOrder.base.name : null;
                let productName = activeOrder.product ? activeOrder.product.name : null;
                let packageName = activeOrder.package ? activeOrder.package.name : null;

                if (baseName && productName && packageName) {
                    actions.push({
                        type: actionTypes.SERVER_REQUEST,
                        payload: {
                            type: methods.METHOD_GET_PRODUCT_IMAGE,
                            params: {
                                productName: productName,
                                baseName: baseName,
                                packageName: packageName,
                                online: state$.value.settings.imagesSource === settings.SOURCE_ONLINE
                            }
                        },
                        method: handlers.GET_LIGHT_TO_DISPENSE_PRODUCT_IMAGE,
                        tabId: payload.tabId
                    });
                }
            }

            return actions;
        })
    );

const savePlainOrderDetail = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.TO_DISPENSE_SAVE_PLAIN_ORDER_DETAIL),
        switchMap(({payload}) => {
            if (state$.value.module.redlikeLightSoftware) {
                return [
                    {
                        type: actionTypes.SET_ACTIVE_TAB_SCENE,
                        payload: {
                            tabId: payload.tabId,
                            scene: componentTypes.DETAIL_SCENE
                        }
                    }
                ];
            }

            return [];
        })
    );

const myOrdersChangeSorting = (action$, state$) =>
    action$.pipe(
        ofType(
            actionTypes.MY_ORDERS_SAVE_SORTING,
            actionTypes.MY_ORDERS_SAVE_FILTER,
            actionTypes.MY_ORDERS_LIST_UPDATE
        ),
        switchMap(({payload}) => {
            let myOrdersParams = filteringHelper.getFilteringParams(payload.tabId, state$.value);

            return [
                {
                    type: actionTypes.SERVER_REQUEST,
                    payload: {
                        type: methods.METHOD_MY_ORDERS_GET_DATA,
                        params: {
                            products: myOrdersParams.productList,
                            fandecks: myOrdersParams.fandeckList,
                            colors: myOrdersParams.formulaList,
                            bases: myOrdersParams.baseList,
                            packages: myOrdersParams.packageList,
                            customers: myOrdersParams.customerList,
                            statusFilter: myOrdersParams.statusList.length
                                ? myOrdersParams.statusList
                                : orderStatus.MY_ORDER_STATUS_LIST,
                            dateFrom: myOrdersParams.dateFrom,
                            dateTo: myOrdersParams.dateTo,
                            search: state$.value.myOrders.filter,
                            searchByColumns: myOrdersParams.searchByColumns,
                            searchFields: formatHelper.getOrderSearchItemsForServer(
                                state$.value.settings.orderSearchItemsEnabled
                            ),
                            orderBy: state$.value.myOrders.sortingCriterion,
                            asc: state$.value.myOrders.sortingAsc,
                            offset: 0,
                            rowCount: config.MAX_ORDERS_LIMIT
                        }
                    },
                    method: handlers.MY_ORDERS_GET_DATA,
                    tabId: payload.tabId
                }
            ];
        })
    );

const updateToDispense = (action$, state$) =>
    action$.pipe(
        ofType(
            actionTypes.TO_DISPENSE_SAVE_SORTING,
            actionTypes.TO_DISPENSE_SAVE_FILTER,
            actionTypes.TO_DISPENSE_SAVE_FILTER_TYPE,
            actionTypes.TO_DISPENSE_LIST_UPDATE
        ),
        switchMap(({payload}) => {
            let toDispenseOrdersParams = filteringHelper.getFilteringParams(payload.tabId, state$.value);

            return [
                {
                    type: actionTypes.SERVER_REQUEST,
                    payload: {
                        type: methods.METHOD_TO_DISPENSE_GET_ORDERS,
                        params: {
                            products: toDispenseOrdersParams.productList,
                            fandecks: toDispenseOrdersParams.fandeckList,
                            colors: toDispenseOrdersParams.formulaList,
                            bases: toDispenseOrdersParams.baseList,
                            packages: toDispenseOrdersParams.packageList,
                            customers: toDispenseOrdersParams.customerList,
                            statusFilter: toDispenseOrdersParams.statusList.length
                                ? toDispenseOrdersParams.statusList
                                : orderStatus.TO_DISPENSE_ORDER_STATUS_LIST,
                            dateFrom: toDispenseOrdersParams.dateFrom,
                            dateTo: toDispenseOrdersParams.dateTo,
                            search: state$.value.toDispense.filter,
                            searchByColumns: toDispenseOrdersParams.searchByColumns,
                            searchFields: formatHelper.getOrderSearchItemsForServer(
                                state$.value.settings.orderSearchItemsEnabled
                            ),
                            orderBy: state$.value.module.gammaSoftware
                                ? state$.value.settings.intergammaMachineSortingOrders
                                : state$.value.toDispense.sortingCriterion,
                            asc: state$.value.toDispense.sortingAsc,
                            offset: 0,
                            rowCount: config.MAX_ORDERS_LIMIT,
                            availableOnly: state$.value.module.weberSoftware,
                            maxNominalAmount: toDispenseOrdersParams.maxNominalAmount,
                            minNominalAmount: toDispenseOrdersParams.minNominalAmount
                        }
                    },
                    method: handlers.TO_DISPENSE_GET_ORDERS,
                    tabId: payload.tabId
                }
            ];
        })
    );

const ordersHistoryUpdate = (action$, state$) =>
    action$.pipe(
        ofType(
            actionTypes.ORDERS_HISTORY_SAVE_SORTING,
            actionTypes.ORDERS_HISTORY_SAVE_FILTER,
            actionTypes.ORDERS_HISTORY_SAVE_FILTER_TYPE,
            actionTypes.ORDERS_HISTORY_LIST_UPDATE
        ),
        switchMap(({payload}) => {
            let historicOrdersParams = filteringHelper.getFilteringParams(
                payload.tabId,
                state$.value,
                settings.NAV_ITEM_ORDERS_HISTORY
            );

            return [
                {
                    type: actionTypes.SERVER_REQUEST,
                    payload: {
                        type: methods.METHOD_TO_DISPENSE_GET_ORDERS,
                        params: {
                            asc: state$.value.ordersHistory.sortingAsc,
                            offset: 0,
                            orderBy: state$.value.ordersHistory.sortingCriterion,
                            rowCount: config.MAX_ORDERS_LIMIT,
                            statusFilter: orderStatus.HISTORICAL_ORDER_STATUS_LIST,
                            search: state$.value.ordersHistory.filter,
                            searchFields: formatHelper.getOrderSearchItemsForServer(
                                state$.value.settings.orderSearchItemsEnabled
                            ),
                            maxNominalAmount: historicOrdersParams.maxNominalAmount,
                            minNominalAmount: historicOrdersParams.minNominalAmount,
                            products: historicOrdersParams.productList
                        }
                    },
                    method: handlers.ORDERS_HISTORY_GET_ORDERS,
                    tabId: payload.tabId
                }
            ];
        })
    );

const getToDispenseOrdersNotification = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.TO_DISPENSE_PROCESS_NOTIFICATION_ORDERS),
        switchMap(({payload}) => {
            let newOrder, order;
            let newReceivedOrder = null;
            let newOrdersCounter = state$.value.toDispense.newOrders;
            let isNew = false;

            for (newOrder of payload.notificationOrders) {
                isNew = true;
                for (order of state$.value.toDispense.notificationOrders) {
                    if (newOrder.id === order.id) {
                        isNew = false;
                    }
                }

                if (isNew) {
                    newOrdersCounter++;
                    newReceivedOrder = newOrder;
                }
            }

            if (state$.value.toDispense.newOrders === -1) {
                newOrdersCounter = 0;
            }

            let actions = [
                {
                    type: actionTypes.TO_DISPENSE_SAVE_NOTIFICATION_ORDERS,
                    payload: {
                        notificationOrders: payload.notificationOrders,
                        newOrdersCounter: newOrdersCounter
                    }
                },
                {
                    type: actionTypes.TO_DISPENSE_LIST_UPDATE,
                    payload: {
                        tabId: payload.tabId
                    }
                }
            ];

            if (
                state$.value.login.loggedUser.canRead(resources.COMPONENT_ORDER_NOTIFICATION_MODAL) &&
                newOrdersCounter > 0 &&
                state$.value.settings.orderingEnabled
            ) {
                if (state$.value.settings.soundEnabled) {
                    let audio = new Audio(successfulOrder);
                    audio.play();
                }

                if (state$.value.settings.orderModalEnabled) {
                    actions.push({
                        type: actionTypes.NAVIGATION_OPEN_MODAL,
                        payload: {
                            tabId: componentTypes.MODAL_NO_TAB,
                            type: componentTypes.ORDER_NOTIFICATION_MODAL,
                            params: {order: newReceivedOrder}
                        }
                    });
                }
            }

            return actions;
        })
    );

const openToDispenseOrderInNewTab = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.OPEN_TO_DISPENSE_ORDER_IN_NEW_TAB),
        switchMap(({payload}) => {
            let toDispenseTab = reducerHelper.getTabByType(
                state$.value.navigation.tabList,
                componentTypes.TO_DISPENSE_TAB
            );

            if (toDispenseTab !== null) {
                return [
                    {
                        type: actionTypes.SERVER_REQUEST,
                        payload: {
                            type: methods.METHOD_GET_ORDER_DETAIL,
                            params: {
                                identifier: payload.identifier
                            }
                        },
                        method: handlers.TO_DISPENSE_GET_DETAIL,
                        tabId: toDispenseTab.id
                    },
                    {
                        type: actionTypes.SET_ACTIVE_TAB,
                        payload: {
                            tabId: toDispenseTab.id
                        }
                    }
                ];
            }

            let newTabId = reducerHelper.getNewTabId(state$.value.navigation.tabList);

            return [
                {
                    type: actionTypes.MENU_ADD_NEW_TO_DISPENSE_TAB,
                    payload: {
                        tabId: newTabId
                    }
                },
                {
                    type: actionTypes.SERVER_REQUEST,
                    payload: {
                        type: methods.METHOD_GET_ORDER_DETAIL,
                        params: {
                            identifier: payload.identifier
                        }
                    },
                    method: handlers.TO_DISPENSE_GET_DETAIL,
                    tabId: newTabId
                }
            ];
        })
    );

const startDispensing = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.TO_DISPENSE_START_DISPENSING),
        switchMap(({payload}) => {
            if (state$.value.toDispense.activeOrder === null) {
                return [];
            }

            return [
                dispensingValidationActions.getValidationActionsSequence(
                    payload.tabId,
                    toDispenseTabActions.addColorStatsFromOrderAndDispense,
                    state$.value.toDispense.validation,
                    state$.value.settings.dispensingWithErrorsEnabled,
                    state$.value.toDispense.activeOrder.formula.realColorantList,
                    state$.value.toDispense.activeOrder.packageCount -
                        state$.value.toDispense.activeOrder.dispensedPackageCount
                )
            ];
        })
    );

const addColorStatsFromOrder = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.ADD_COLOR_STATS_FROM_ORDER_AND_DISPENSE),
        switchMap(({payload}) => {
            if (state$.value.toDispense.activeOrder === null) {
                return [];
            }

            return [rccActions.startOrderDispensing(payload.tabId, state$.value.toDispense.activeOrder.id)];
        })
    );

export default [
    saveOrderDetail,
    savePlainOrderDetail,
    myOrdersChangeSorting,
    updateToDispense,
    ordersHistoryUpdate,
    getToDispenseOrdersNotification,
    openToDispenseOrderInNewTab,
    startDispensing,
    addColorStatsFromOrder
];
